import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
/*
 * The design system FIRST, then the app. `App` pulls in every screen and each
 * screen's own stylesheet; those must come AFTER `tokens.css` (which imports
 * `components.css`), so a screen's class beats a `ui-*` class it sits beside
 * — the cascade the inline styles used to win by default.
 * `ui/cssOrder.test.ts` holds it.
 */
import './ui/tokens.css';
import { App } from './app/App';
// Print rules. Inside `@media print` in full, so nothing changes on screen.
import './visitor/print.css';
import { prepareServiceWorker } from './offline/register';
import { releaseHop, runReleaseHop } from './offline/raveSlots';
import { followNewBuilds } from './lib/staleBuild';
import { followNewBuild } from './lib/newBuild';
import { isFramed } from './lib/framed';

// Before anything lazy loads: a chunk from an older deploy reloads the page once.
followNewBuilds();

const root = document.getElementById('root');
if (!root) throw new Error('no #root element');

// A hop that frees this old rave's offline slot (the iPhone app, `raveSlots.ts`): nothing renders, it moves on.
const hop = releaseHop(window.location.href);
if (hop) void runReleaseHop(hop);
else createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/*
 * Inside the setup wizard's phone preview (`isFramed`) the app never reloads
 * ITSELF — no service worker, no new-version check: the wizard reloads it
 * after a save or on "Reload". A deploy used to reload the preview under the
 * person's eyes, again and again on a busy day (the user, 2026-10-11).
 */
if (!isFramed() && !hop) {
  // After first paint: an address that keeps nothing gives its worker back; a rave decides in `RaveOffline`.
  void prepareServiceWorker();
  // An open page — a phone app in the background — follows the newest deploy.
  followNewBuild();
}
